import React, { useState } from 'react';

import { downloadZip } from '@apis/common';
import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import { dashboardPaymentSlipBatchApis } from '@apis/next/PaymentSlipBatchApis';
import { PaymentSlipTable } from '@components/admin/booking/PaymentSlipTable';
import { FormTitle } from '@components/admin/Forms';
import { PaymentSlipAddDialog } from '@components/admin/invoice/PaymentSlipAddDialog';
import { PaymentSlipBatchFormDialog } from '@components/admin/invoice/PaymentSlipBatchFormDialog';
import { PaymentSlipSimpleList } from '@components/admin/invoice/PaymentSlipSimpleList';
import { Layout } from '@components/admin/Layout';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { VerticalSpace } from '@components/VerticalSpace';
import { moneyStrings } from '@data/money';
import { paymentSlipBatchStrings } from '@data/paymentSlipBatch';
import { useSearch } from '@hooks/next/useSearch';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import { RemoteFile } from '@models/next/FileType';
import { PaymentSlip } from '@models/next/PaymentSlip';
import {
  initialPaymentSlipSearchQueryForBatch,
  PaymentSlipBatchSearchType,
  PaymentSlipBatchHelper,
} from '@models/next/PaymentSlipBatch';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getBreadcrumb } from '@utils/pages';
import { formatDate, mapOptional } from '@utils/types';
import { useChecklist } from '@utils/useChecklist';
import clsx from 'clsx';
import Color from 'color';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: () => ({
      remove: `${paymentSlipBatchStrings.payment_slip_batch}を${paymentSlipBatchStrings.remove}する`,
      delete: `${paymentSlipBatchStrings.payment_slip_batch}を${paymentSlipBatchStrings.delete}する`,
    }),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    flex: 1,
    padding: '1.5rem 2rem',
    ...common.verticalSpace3,
  },
  table: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: '120px 1fr 120px 1fr',
    '& > div': {
      padding: '0.75rem',
    },
    '& > div:nth-child(2)': {
      gridColumn: '2 / span 3',
      borderRight: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).string()}`,
      borderTop: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).string()}`,
    },
    '& > div:nth-child(even)': {
      borderBottom: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).string()}`,
    },
    '& > div:nth-child(odd)': {
      ...common.fontWeight700,
      ...common.textAlignCenter,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).string(),
    },
    '& > div:nth-child(4n+2)': {
      borderRight: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).string()}`,
    },
  },
  textContainer: {
    padding: '1rem 3rem',
  },
}));

type confirmType = 'remove' | 'delete';

interface PaymentSlipEditConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  type: confirmType;
  onSuccess?: () => void;
  checkedItems?: PaymentSlip[];
  items?: PaymentSlip[];
}

export const PaymentSlipBatchConfirmDialog: React.FC<PaymentSlipEditConfirmDialogProps> = (
  props,
) => {
  const { open, onClose, type, items = [], checkedItems = [], onSuccess } = props;

  const checkedItemIds = checkedItems.map((d) => d.id);

  const { search } = useSearch(PaymentSlipBatchSearchType);

  const queryClient = useQueryClient();

  const history = useHistory();

  const classes = { ...useStyles(), ...useCommonStyles() };

  const patchPaymentSlipBatch = dashboardPaymentSlipBatchApis.usePatch(
    '/dashboard/payment-slip-batch/:id',
    {
      params: { id: search.id },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardPaymentSlipBatchApis.getKeyByPath('get', '/dashboard/payment-slip-batch/:id', {
            params: { id: search.id },
          }),
        );
        queryClient.invalidateQueries(
          dashboardPaymentSlipApis.getKeyByPath('get', '/dashboard/payment-slip'),
        );
        onSuccess?.();
        onClose();
      },
    },
  );

  const deletePaymentSlipBatch = dashboardPaymentSlipBatchApis.useDelete(
    '/dashboard/payment-slip-batch/:id',
    {
      params: { id: search.id },
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          dashboardPaymentSlipBatchApis.getKeyByPath('get', '/dashboard/payment-slip-batch/:id', {
            params: { id: search.id },
          }),
        );
        queryClient.invalidateQueries(
          dashboardPaymentSlipBatchApis.getKeyByPath('get', '/dashboard/payment-slip-batch'),
        );
        queryClient.invalidateQueries(
          dashboardPaymentSlipApis.getKeyByPath('get', '/dashboard/payment-slip'),
        );
        history.replace('/admin/invoice/PaymentSlipBatchList');
      },
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [deletePaymentSlipBatch, patchPaymentSlipBatch],
  });

  return (
    <ActionableDialog
      open={open}
      onClose={onClose}
      size="lg"
      source={import.meta.url}
      muiDialogProps={{ disablePortal: false }}
    >
      <ActionableDialogTitle textAlign="center">{strings.title()[type]}</ActionableDialogTitle>

      <ActionableDialogContent>
        <div className={classes.colSpanFull}>{renderAlertsAndSpinnerModal()}</div>
        {type === 'remove' && (
          <div style={{ padding: '12px 0' }}>
            {paymentSlipBatchStrings.pleaseConfirmRemoveFrom(search.batchNo)}
            <VerticalSpace height={10} />
            <div className={classes.middleText}>
              <PaymentSlipSimpleList items={checkedItems} />
            </div>
          </div>
        )}
        {type === 'delete' && (
          <div className={classes.textContainer}>
            {paymentSlipBatchStrings.batchIsDeleted(search.batchNo)}
            <VerticalSpace height={10} />
            <div className={classes.textError}>
              {paymentSlipBatchStrings.operationCannotBeUndone}
            </div>
            <VerticalSpace height={30} />
            {paymentSlipBatchStrings.isItOkToDelete}
          </div>
        )}
      </ActionableDialogContent>
      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.prev}
          style={{ width: 90 }}
          onClick={onClose}
        />
        <Button
          color="accent"
          size="small"
          shape="round"
          type="button"
          text={type === 'remove' ? formStrings.remove : formStrings.delete}
          style={{ width: 90 }}
          onClick={() => {
            if (type === 'remove') {
              patchPaymentSlipBatch.mutateAsync({
                payment_slip_ids: items
                  .filter((ps) => !checkedItemIds.includes(ps.id))
                  .map((d) => d.id),
              });
            } else {
              deletePaymentSlipBatch.mutateAsync({});
            }
          }}
        />
      </ActionableDialogActions>
    </ActionableDialog>
  );
};

export const PaymentSlipBatchDetails: React.FC = () => {
  const { search } = useSearch(PaymentSlipBatchSearchType);

  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [addPaymentSlipDialogOpen, setAddPaymentSlipDialogOpen] = useState(false);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [type, setType] = useState<confirmType>('remove');

  const classes = { ...useStyles(), ...useCommonStyles() };

  const paymentSlipBatchDetails = dashboardPaymentSlipBatchApis.useGet(
    '/dashboard/payment-slip-batch/:id',
    {
      params: { id: search.id },
    },
  );

  const checklist = useChecklist(
    [],
    paymentSlipBatchDetails.data?.payment_slips.map((d) => d.id) ?? [],
  );

  const confirmDialog = useConfirmDialog();

  const helper = mapOptional(paymentSlipBatchDetails.data, (d) => new PaymentSlipBatchHelper(d));

  const canEditBatch = helper?.canEdit();

  const getFileLink = (file: RemoteFile[] | undefined | null, fileName: string) => {
    if (!file || file.length === 0) return '';
    return file.length === 1 ? (
      <a href={file[0].url} target="_blank" rel="noreferrer" className={classes.link}>
        {fileName}
      </a>
    ) : (
      <a
        target="_blank"
        rel="noreferrer"
        className={clsx(classes.link, classes.baseButton)}
        onClick={() => {
          downloadZip(
            file.map((f) => f.url),
            fileName,
          );
        }}
      >
        {fileName}
      </a>
    );
  };

  const checkEdit = async () => {
    if (canEditBatch?._tag !== 'Right') {
      await confirmDialog.confirm({
        title: formStrings.error,
        message: canEditBatch?.left.messages.join('\n'),
      });

      return false;
    }
    return true;
  };

  return (
    <Layout
      title={`${paymentSlipBatchStrings.batchDetails} - ${search.batchNo}`}
      breadcrumb={[...getBreadcrumb('/admin/invoice/PaymentSlipBatchList'), search.batchNo]}
      loading={paymentSlipBatchDetails.isLoading}
    >
      {confirmDialog.renderDialog()}
      <PaymentSlipBatchConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        type={type}
        items={paymentSlipBatchDetails.data?.payment_slips}
        checkedItems={paymentSlipBatchDetails.data?.payment_slips.filter((d) =>
          checklist.checked(d.id),
        )}
        onSuccess={() => checklist.setChecklist([])}
      />
      <PaymentSlipAddDialog
        open={addPaymentSlipDialogOpen}
        onClose={() => setAddPaymentSlipDialogOpen(false)}
        initialQuery={initialPaymentSlipSearchQueryForBatch}
        paymentSlipBatch={paymentSlipBatchDetails.data}
      />
      <PaymentSlipBatchFormDialog
        type="edit"
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        initialPaymentSlipBatch={paymentSlipBatchDetails.data}
      />
      <div className={classes.container}>
        <FormTitle
          title={paymentSlipBatchStrings.batchInfo}
          noExpand
          onChangeEditting={async () => {
            if (await checkEdit()) {
              setEditDialogOpen(true);
            }
          }}
        />
        <div className={classes.table}>
          <div>{paymentSlipBatchStrings.batch_no}</div>
          <div>{paymentSlipBatchDetails.data?.batch_no || '-'}</div>
          <div>{paymentSlipBatchStrings.totalAmount}</div>
          <div>{helper ? moneyStrings.formatYenFront(helper.getTotalAmount()) : '-'}</div>
          <div>{paymentSlipBatchStrings.payment_status || '-'}</div>
          <div>{paymentSlipBatchDetails.data?.payment_status || '-'}</div>
          <div>{paymentSlipBatchStrings.payee}</div>
          <div>{paymentSlipBatchDetails.data?.payee.name_en || '-'}</div>
          <div>{paymentSlipBatchStrings.payee_ja}</div>
          <div>{paymentSlipBatchDetails.data?.payee.name_ja || '-'}</div>
          <div>{paymentSlipBatchStrings.deadline}</div>
          <div>{formatDate(paymentSlipBatchDetails.data?.deadline) || '-'}</div>
          <div>{paymentSlipBatchStrings.payment_date}</div>
          <div>{formatDate(paymentSlipBatchDetails.data?.payment_date) || '-'}</div>
          <div>{paymentSlipBatchStrings.withdraw_no}</div>
          <div>{paymentSlipBatchDetails.data?.withdraw_no || '-'}</div>
          <div>{paymentSlipBatchStrings.withdraw_date}</div>
          <div>{formatDate(paymentSlipBatchDetails.data?.withdraw_date) || '-'}</div>
          <div>{paymentSlipBatchStrings.invoice_files}</div>
          <div>
            {getFileLink(
              paymentSlipBatchDetails.data?.invoice_files,
              paymentSlipBatchStrings.invoice_files,
            ) || '-'}
          </div>
          <div>{paymentSlipBatchStrings.withdraw_certificate_files}</div>
          <div>
            {getFileLink(
              paymentSlipBatchDetails.data?.withdraw_certificate_files,
              paymentSlipBatchStrings.withdraw_certificate_files,
            ) || '-'}
          </div>
        </div>
      </div>
      <div className={classes.container}>
        <FormTitle title={paymentSlipBatchStrings.paymentSlipList} noExpand noEdit />
        {paymentSlipBatchDetails.data && paymentSlipBatchDetails.data?.payment_slips.length > 0 && (
          <PaymentSlipTable
            items={paymentSlipBatchDetails.data?.payment_slips}
            checklist={checklist}
          />
        )}
        <div className={clsx(classes.flexInline, classes.horizontalSpace1)}>
          <Button
            size="small"
            shape="round"
            text={paymentSlipBatchStrings.add}
            type="button"
            color="primary"
            onClick={async () => {
              if (await checkEdit()) {
                setAddPaymentSlipDialogOpen(true);
              }
            }}
          />
          <Button
            size="small"
            shape="round"
            text={paymentSlipBatchStrings.remove}
            type="button"
            color="primary"
            disabled={checklist.checklist.length === 0}
            onClick={async () => {
              if (await checkEdit()) {
                setType('remove');
                setConfirmDialogOpen(true);
              }
            }}
          />
        </div>
        <Button
          size="small"
          shape="round"
          text={
            <>
              　{paymentSlipBatchStrings.batch}
              {paymentSlipBatchStrings.delete}　
            </>
          }
          type="button"
          color="grey"
          onClick={async () => {
            if (await checkEdit()) {
              setType('delete');
              setConfirmDialogOpen(true);
            }
          }}
        />
      </div>
    </Layout>
  );
};
